import { equipItem, useItem } from "../systems/LootSystem.js";
import { addItem, canAdd, takeOne, stackCount, isStackable, stackKey, MAX_STACK, slotCount, tidyBag } from "../systems/Inventory.js";
import { sectionTier } from "../config/helix.js";
import { SKINS, PRESTIGE_SKIN, skinUnlocked, skinRequirement, featArt,
  achievementsFor, achieveProgress, earnedCount, achievementCount, heroEarnedCount, heroAchievements } from "../config/achievements.js";
import { ELEMENTS, ELEMENT_LIST, starterKit, forgeWeapon, enchantWeapon, reinforceArmor, makePotion, sellPrice, isSellable } from "../items/ItemFactory.js";
import { CHARACTERS } from "../config/characters.js";
import { memTier, memSummary, memProgress } from "../systems/RelicMemory.js";
import { affinityFor } from "../config/relics.js";
import { getCodex, getMemory, codexComplete, codexProgress, completedCodexCount, totalCollected, CODEX_COUNT, TOTAL_MEMORIES } from "../config/memories.js";
import { RESEARCH, BRANCHES } from "../config/research.js";
import { PETS } from "../config/pets.js";
import { gi } from "./glyphs.js";
import { ShareCard } from "./ShareCard.js";
import { FEATURES } from "../config/features.js";

const HOTBAR_ITEM_SLOTS = 8;

// Small inline lock glyph (replaces the 🔒 emoji) — inherits text colour.
const LOCK = `<svg class="ico-lock" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5zm-3 5a3 3 0 1 1 6 0v3H9V7zm3 6.2a1.4 1.4 0 0 1 .8 2.55V17.4a.8.8 0 0 1-1.6 0v-1.65A1.4 1.4 0 0 1 12 13.2z"/></svg>`;

function weaponGlyph(w) {
  const m = (w && w.model) || "";
  if (/bow/.test(m)) return gi("bow");
  if (/staff|wand/.test(m)) return gi("staff");
  if (/axe/.test(m)) return gi("axe");
  if (/dagger/.test(m)) return gi("dagger");
  return gi("sword");
}
function itemGlyph(it) {
  if (!it) return "·";
  switch (it.kind) {
    case "weapon": return weaponGlyph(it);
    case "armor": return gi("shield");
    case "potion": return gi("potion");
    case "coins": return gi("coins");
    case "resource": return gi("resource");
    case "fragment": return gi("fragment");
    case "treasure": return gi("treasure");
    default: return "▫";
  }
}
// role → theme-aware glyph (emoji, or SVG in the Relikara theme)
const ROLE_GLYPH = { collector: "magnet", hunter: "flame", warden: "shield", channeler: "spark", duelist: "sword", forager: "treasure" };
function roleGlyph(role) { return gi(ROLE_GLYPH[role] || "paw"); }
const isUsable = (it) => it && (it.kind === "weapon" || it.kind === "armor" || it.kind === "potion" || it.kind === "fragment");
const badgeFor = (it) => it.kind === "weapon" ? `${it.dmgMin}-${it.dmgMax}` : it.kind === "armor" ? `+${it.def}`
  : it.kind === "potion" ? `+${it.heal}` : it.kind === "food" ? `♥${it.heal || 0}` : it.kind === "treasure" ? `${it.value}g` : it.amount ? `x${it.amount}` : "";

/**
 * HUD: health/floor/gold labels, the always-visible inventory bar (hotbar), the
 * inventory panel, minimap toggle, and screens. Slots show a rendered thumbnail
 * of the actual item model (via game.icons) with the emoji as fallback.
 */
export class HUD {
  constructor(game) {
    this.game = game;
    const id = (x) => document.getElementById(x);
    this.el = {
      hud: id("hud"), healthFill: id("healthFill"), healthText: id("healthText"),
      staminaFill: id("staminaFill"), staminaText: id("staminaText"),
      manaBar: id("manaBar"), manaFill: id("manaFill"), manaText: id("manaText"),
      gear: id("gearPanel"), floor: id("floorLabel"), enemies: id("enemyLabel"), kills: id("killLabel"), currency: id("currencyBar"),
      heroLevel: id("heroLevel"), xpFill: id("xpFill"),
      controls: id("controlsHint"), hotbar: id("hotbar"), inventory: id("inventory"), minimap: id("minimap"), minimapLegend: id("minimapLegend"),
      start: id("startScreen"), death: id("deathScreen"), deathStats: id("deathStats"),
      loading: id("loading"), assetNote: id("assetNote"),
      character: id("characterScreen"), charCards: id("charCards"),
      intermission: id("intermissionScreen"), prompt: id("interactPrompt"),
      recap: id("recapScreen"),
      chestModal: id("chestModal"), chestTitle: id("chestTitle"), chestSlots: id("chestSlots"), chestTakeAll: id("chestTakeAll"),
    };
    this.el.controls.textContent = "[LMB] Attack   [RMB] Block   [Shift] Roll   [Q] Ability   [F] Loot   [1-8] Use   [H] Heal   [E] Inventory";

    // live companion panel (created once, updated each frame during play)
    this.el.pets = document.createElement("div");
    this.el.pets.id = "petPanel";
    (document.getElementById("hud") || document.body).appendChild(this.el.pets);

    // signature-ability widget (glyph + cooldown sweep), bottom-centre near the hotbar
    this.el.ability = document.createElement("div");
    this.el.ability.id = "abilityWidget";
    this.el.ability.title = "Signature ability (Q)";
    this.el.ability.innerHTML =
      `<div class="ab-face"><div class="ab-glyph"></div><div class="ab-cd"></div><div class="ab-count"></div>` +
      `<div class="ab-key">Q</div><div class="ab-mana"></div></div><div class="ab-name"></div>`;
    (document.getElementById("hud") || document.body).appendChild(this.el.ability);
    this.el.ability.onclick = () => this.game._useAbility();
    document.getElementById("startBtn").onclick = () => this.showCharacterSelect();
    document.getElementById("restartBtn").onclick = () => this.game.returnToMenu();
    this.share = new ShareCard(this.game);
    const shareRunBtn = document.getElementById("shareRunBtn");
    if (shareRunBtn) {
      shareRunBtn.style.display = FEATURES.sharing ? "" : "none";   // hidden for now; flip FEATURES.sharing to bring back
      shareRunBtn.onclick = () => this.share.shareRun(this._lastRun || { depth: this.game.floor, kills: this.game.totalKills });
    }
    if (this.el.chestTakeAll) this.el.chestTakeAll.onclick = () => this._takeAllChest();
    const chestCloseBtn = document.getElementById("chestClose");
    if (chestCloseBtn) chestCloseBtn.onclick = () => this.closeChest();
    this.el.pause = document.getElementById("pauseScreen");
    const resumeBtn = document.getElementById("resumeBtn");
    const saveExitBtn = document.getElementById("saveExitBtn");
    if (resumeBtn) resumeBtn.onclick = () => this.game.togglePause();
    if (saveExitBtn) saveExitBtn.onclick = () => this.game.saveAndExit();
    const bananaBtn = document.getElementById("bananaToggle");
    if (bananaBtn) {
      const bs = this.game.bananaScare;
      if (!bs || !bs.canToggle()) {
        bananaBtn.style.display = "none";   // production: always on, toggle hidden
      } else {
        const syncBanana = () => {
          const on = bs.isEnabled();
          bananaBtn.textContent = `🍌 Banana Jump Scare: ${on ? "On" : "Off"}`;
          bananaBtn.classList.toggle("on", !!on);
        };
        bananaBtn.onclick = () => { bs.toggle(); syncBanana(); };
        syncBanana();
      }
    }
    const back = document.getElementById("charBackBtn");
    if (back) back.onclick = () => { this.el.character?.classList.add("hidden"); this.el.start.classList.remove("hidden"); };
    const diffEl = document.getElementById("difficulty");
    if (diffEl) diffEl.querySelectorAll(".diff-btn").forEach((b) => {
      b.onclick = () => {
        diffEl.querySelectorAll(".diff-btn").forEach((x) => x.classList.remove("active"));
        b.classList.add("active");
        game.setDifficulty(b.dataset.diff);
      };
    });
    this._sig = "";
    this._selected = null;   // { kind:"bag", idx } | { kind:"weapon" } | { kind:"armor" }
  }

  setAssetNote(loaded, total, missing = []) {
    if (missing && missing.length) {
      this.el.assetNote.innerHTML =
        `⚠ Loaded ${loaded}/${total} assets — ${missing.length} file(s) did not load (likely still cloud-only in OneDrive). ` +
        `Press F12 for the list, then right-click <code>public/assets</code> → “Always keep on this device” and hard-refresh.`;
    } else {
      this.el.assetNote.textContent = `Loaded ${loaded} assets.`;
    }
  }

  showLoading(v) { this.el.loading.classList.toggle("hidden", !v); }
  showStart() {
    this.el.start.classList.remove("hidden");
    this.el.character?.classList.add("hidden"); this.el.death?.classList.add("hidden");
    this.el.intermission?.classList.add("hidden"); this.el.prompt?.classList.add("hidden");
    this.el.hud.classList.add("hidden"); this.el.hotbar.classList.add("hidden"); this.el.minimap?.classList.add("hidden");
    this.el.controls?.classList.add("hidden"); this.el.inventory?.classList.add("hidden");
    if (this.el.pets) this.el.pets.style.display = "none";
  }
  showPause() { this.el.pause?.classList.remove("hidden"); }
  hidePause() { this.el.pause?.classList.add("hidden"); }

  /** Transient bottom-center notice (errors, save failures). Lazily creates its DOM node. */
  toast(msg, ms = 3200) {
    let el = document.getElementById("toast");
    if (!el) { el = document.createElement("div"); el.id = "toast"; document.body.appendChild(el); }
    el.textContent = msg; el.classList.add("show");
    clearTimeout(this._toastT);
    this._toastT = setTimeout(() => el.classList.remove("show"), ms);
  }

  /** Story recap at run start. `data` from Recap.buildRecap; `onBegin` dismisses. */
  showRecap(data, onBegin) {
    const scr = this.el.recap; if (!scr) { onBegin && onBegin(); return; }
    const secs = data.sections.map((s) =>
      `<div class="recap-sec"><h3>${s.heading}</h3>${s.lines.map((l) => `<p>${l}</p>`).join("")}</div>`).join("");
    const facts = data.facts && data.facts.length
      ? `<div class="recap-sec"><h3>Curiosities</h3><ul class="recap-facts">${data.facts.map((f) => `<li>${f}</li>`).join("")}</ul></div>` : "";
    // accent follows the active UI theme (relikara purple / classic gold), set in CSS
    scr.innerHTML = `<div class="recap-box">
      <div class="recap-title">${data.title}</div>
      <div class="recap-scroll">${secs}${facts}</div>
      <div class="recap-send">${data.sendoff}</div>
      <button class="btn recap-begin" id="recapBegin">Continue ▸</button>
    </div>`;
    scr.classList.remove("hidden");
    const done = () => { scr.classList.add("hidden"); onBegin && onBegin(); };
    const btn = scr.querySelector("#recapBegin"); if (btn) btn.onclick = done;
  }
  hideScreens() {
    this.el.start.classList.add("hidden"); this.el.death.classList.add("hidden"); this.el.character?.classList.add("hidden");
    this.el.intermission?.classList.add("hidden"); this.el.prompt?.classList.add("hidden");
    this.el.hud.classList.remove("hidden"); this.el.hotbar.classList.remove("hidden"); this.el.minimap?.classList.remove("hidden");
    this.el.controls?.classList.remove("hidden");
    this._sig = "";
  }

  // ---------- hub / NPC interaction UI ----------
  /** Bottom prompt shown when standing near an NPC. */
  setInteractPrompt(npc) {
    const el = this.el.prompt; if (!el) return;
    if (npc) {
      const key = { blacksmith: "hex/blacksmith", shop: "hex/shop", home: "hex/home" }[npc.type];
      const url = key && this.game.icons ? this.game.icons.iconFor({ model: key }) : null;
      const verb = npc.type === "helix" ? "Study" : "Speak to";
      el.innerHTML = `${url ? `<img class="prompt-icon" src="${url}" alt="">` : ""}<span><b>E</b> · ${verb} ${npc.name}</span>`;
      el.classList.remove("hidden");
    } else el.classList.add("hidden");
  }

  /** Bottom prompt for a lockable chest / door in the dungeon (reuses the NPC
   *  prompt element — they're never shown at the same time). */
  setLockPrompt(info) {
    const el = this.el.prompt; if (!el) return;
    if (!info) { if (this._lockPromptOn) { el.classList.add("hidden"); this._lockPromptOn = false; } return; }
    const afford = !info.keyCost || (info.have || 0) >= info.keyCost;
    const cost = info.keyCost ? ` · <b style="color:${afford ? "#f0c34b" : "#ff8a7a"}">🗝 ${info.keyCost}</b>` : "";
    el.innerHTML = `<span><b>V</b> · ${info.label}${cost}</span>`;
    el.classList.remove("hidden");
    this._lockPromptOn = true;
  }

  /** Open one NPC's panel (blacksmith/shop/home) as the conversation. */
  openStation(type) {
    const scr = this.el.intermission; if (!scr) return;
    this.el.inventory.classList.add("hidden");
    scr.classList.remove("hidden");
    this._hubView = type; this._hubStation = true; this._hubMsg = "";
    this.renderHub();
  }
  closeStation() { this.el.intermission?.classList.add("hidden"); }

  _spend(n) {
    const p = this.game.player;
    if ((p.gold || 0) >= n) { p.gold -= n; return true; }
    this._hubMsg = "Not enough gold.";
    return false;
  }

  renderHub() {
    const g = document.getElementById("hubGold"); if (g) g.textContent = this.game.player.gold || 0;
    const m = document.getElementById("hubMsg"); if (m) m.textContent = this._hubMsg || "";
    const c = document.getElementById("hubContent"); if (!c) return;
    if (this._hubView === "blacksmith") c.innerHTML = this._hubBlacksmithHTML();
    else if (this._hubView === "shop") c.innerHTML = this._hubShopHTML();
    else if (this._homeTabIds().includes(this._hubView)) c.innerHTML = this._homeShellHTML(this._hubView);
    else c.innerHTML = this._hubChoicesHTML();
    // wire
    c.querySelectorAll("[data-open]").forEach((el) => el.onclick = () => { this._hubView = el.dataset.open; this._hubMsg = ""; this.renderHub(); });
    c.querySelectorAll("[data-back]").forEach((el) => el.onclick = () => {
      if (this._hubStation) this.game.closeStation();
      else { this._hubView = "choices"; this._hubMsg = ""; this.renderHub(); }
    });
    c.querySelectorAll("[data-skin]").forEach((el) => el.onclick = () => { this.game.setSkin(el.dataset.skin); this.renderHub(); });
    c.querySelectorAll(".st-row[data-smact]").forEach((el) => el.addEventListener("click", (ev) => {
      if (ev.shiftKey && !ev.target.closest("button")) this._hubAction(el.dataset.smact, el.dataset.sarg);   // shift-click = whole-stack move
    }));
    c.querySelectorAll("[data-descend]").forEach((el) => el.onclick = () => this.game.descend());
    c.querySelectorAll("[data-codex]").forEach((el) => el.onclick = () => { this._helixCodex = +el.dataset.codex; this.renderHub(); });
    c.querySelectorAll("[data-act]").forEach((el) => el.onclick = () => this._hubAction(el.dataset.act, el.dataset.arg));
    const ri = c.querySelector("#redeemInput");
    if (ri) ri.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); this._hubAction("redeem"); } });
    this._wireResearchPan(c);
  }

  /** Click-and-drag to pan the research tree (LucidChart-style). Node unlock
   *  buttons still click normally; dragging empty space or node cards pans. */
  _wireResearchPan(container) {
    const scroll = container.querySelector(".rtree-scroll");
    if (!scroll) return;
    let sx = 0, sy = 0, sl = 0, st = 0, dragging = false;
    const onMove = (e) => {
      if (!dragging) return;
      scroll.scrollLeft = sl - (e.clientX - sx);
      scroll.scrollTop = st - (e.clientY - sy);
    };
    const onUp = () => {
      dragging = false;
      scroll.classList.remove("grabbing");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("blur", onUp);            // alt-tab / focus loss mid-drag
      window.removeEventListener("pointercancel", onUp);
    };
    scroll.addEventListener("mousedown", (e) => {
      if (e.button !== 0 || e.target.closest("button")) return;   // let node buttons click
      dragging = true;
      sx = e.clientX; sy = e.clientY; sl = scroll.scrollLeft; st = scroll.scrollTop;
      scroll.classList.add("grabbing");
      e.preventDefault();
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      window.addEventListener("blur", onUp);               // cancel cleanly if the window loses focus
      window.addEventListener("pointercancel", onUp);
    });
  }

  _hubAction(act, arg) {
    const p = this.game.player, floor = this.game.floor;
    if (act === "forge") {
      if (this.game.smithTier() === 0) this._hubMsg = "Research Apprentice Smithing first.";
      else if ((p.weapon.forges || 0) >= this.game.forgeCountCap()) this._hubMsg = "Forged as far as your Smithing allows — research a higher tier.";
      else {
        const cost = this.game.forgeCost(), cu = this.game.forgeCopperCost();
        if (this.game.copperReserve() < cu) this._hubMsg = `Not enough copper fuel (need ${cu}). Deposit copper.`;
        else if (this._spend(cost)) { this.game._spendCopper(cu); forgeWeapon(p.weapon, this.game.forgeRarityCapIdx()); this._hubMsg = `Forged ${p.weapon.name}.`; }
      }
    }
    else if (act === "enchant") {
      if (!this.game.canEnchant()) this._hubMsg = "Research Runescribing first.";
      else {
        const cost = this.game.enchantCost(), cu = this.game.enchantCopperCost();
        if (this.game.copperReserve() < cu) this._hubMsg = `Not enough copper fuel (need ${cu}). Deposit copper.`;
        else if (this._spend(cost)) { this.game._spendCopper(cu); enchantWeapon(p.weapon, arg, this.game.enchantPowerCap()); this._hubMsg = `${p.weapon.name} enchanted.`; }
      }
    }
    else if (act === "depositcopper") { const n = this.game.depositCopper(); this._hubMsg = n ? `Deposited ${n} copper fuel. Reserve: ${this.game.copperReserve()}.` : "No copper in your bag to deposit."; }
    else if (act === "reinforce") {
      if (!this.game.canReinforce()) this._hubMsg = "Research Armoursmithing first.";
      else { const cost = this.game.reinforceCost(); if (this._spend(cost)) { reinforceArmor(p.armor); p.applyGear(); this._hubMsg = `${p.armor.name} reinforced.`; } }
    }
    else if (act === "research") { this._hubMsg = this.game.tryResearch(arg).msg; }
    else if (act === "store") { if (this.game.depositToStash(+arg)) this._hubMsg = "Stored."; }
    else if (act === "storeone") { if (this.game.depositOneToStash(+arg)) this._hubMsg = "Stored one."; }
    else if (act === "storeall") { this.game.depositAllToStash(); this._hubMsg = "Stored your bag."; }
    else if (act === "take") { if (this.game.withdrawFromStash(+arg)) this._hubMsg = "Withdrawn."; else this._hubMsg = "Bag full."; }
    else if (act === "takeone") { if (this.game.withdrawOneFromStash(+arg)) this._hubMsg = "Took one."; else this._hubMsg = "Bag full."; }
    else if (act === "takeall") { const n = this.game.withdrawAllFromStash(); this._hubMsg = n ? `Took ${n} stack${n > 1 ? "s" : ""}.` : "Bag full."; }
    else if (act === "expandbag") { const r = this.game.expandBag(); this._hubMsg = r.ok ? `Bag expanded to ${r.cap} slots (traded 1 memory).` : r.msg; }
    else if (act === "craft") { const r = this.game.craft(arg); this._hubMsg = r.msg; if (r.ok && this.game.audio) this.game.audio.sfx("magic_charge", 0.4); }
    else if (act === "redeem") { const el = document.getElementById("redeemInput"); const r = this.game.redeemCode(el ? el.value : ""); this._hubMsg = r.msg; if (r.ok && this.game.audio) this.game.audio.sfx("magic_charge", 0.6); }
    else if (act === "pet") { if (this.game.setActiveCompanion(arg)) this._hubMsg = "Companion summoned."; }
    else if (act === "petoff") { this.game.setActiveCompanion(null); this._hubMsg = "Companion dismissed."; }
    else if (act === "wisp") { this.game.toggleWisp(); }
    else if (act === "rest") { if (!this.game.homeRested) { p.hp = p.maxHp; this.game.homeRested = true; this._hubMsg = "Fully rested."; } }
    else if (act === "turnin") {
      const res = this.game.turnInMemories();
      if (!res.count) this._hubMsg = "No memories to offer.";
      else {
        const relicNote = res.relics.length
          ? ` A codex completes — forged ${res.relics.map((r) => r.name).join(", ")}!`
          : "";
        this._hubMsg = `Offered ${res.count} · recovered ${res.fresh} new memor${res.fresh === 1 ? "y" : "ies"} — Helix ${res.helix.sections}/${res.helix.total}.${relicNote}`;
      }
    }
    else if (act === "brew") {
      if (!this.game.canBrew()) this._hubMsg = "Research Provisioning first.";
      else { const cost = 35; const pot = makePotion(floor + (this.game.brewGreater() ? 4 : 2));
        if (!canAdd(p.inv, pot, p.invCap)) this._hubMsg = "Bag full."; else if (this._spend(cost)) { addItem(p.inv, pot, p.invCap); this._hubMsg = this.game.brewGreater() ? "Brewed a Greater potion." : "Brewed a potion."; } } }
    else if (act === "buy") {
      const s = (this.game.shopStock || [])[+arg];
      if (!s || s.sold) return;
      if (!canAdd(p.inv, s.item, p.invCap)) this._hubMsg = "Bag full.";
      else if (this._spend(s.price)) { addItem(p.inv, s.item, p.invCap); s.sold = true; this._hubMsg = `Bought ${s.item.name}.`; }
    }
    else if (act === "buykey") {
      const price = this.game.keyPrice();
      if (this._spend(price)) { p.keys = (p.keys || 0) + 1; this._hubMsg = `Bought a Vault Key. You hold 🗝 ${p.keys}.`; }
    }
    else if (act === "sell") {
      const it = p.inv[+arg];
      if (it && isSellable(it)) {
        const q = stackCount(it);
        const gp = sellPrice(it, floor) * q;
        p.gold = (p.gold || 0) + gp;
        p.inv[+arg] = null;
        this._hubMsg = `Sold ${q > 1 ? q + "× " : ""}${it.name} for ${gp}g.`;
      }
    }
    this._sig = "";
    this.renderHub();
  }

  /** A dynamic NPC line that "knows your name" and reacts to notoriety. */
  _npcLine(type) {
    const name = (this.game.profile && this.game.profile.name) || (this.game.character && this.game.character.name) || "traveller";
    const not = (this.game.meta && this.game.meta.notoriety) || 0;
    const known = not > 400;
    const lines = {
      blacksmith: known
        ? `Back again, ${name}. I've reforged this same blade for a thousand heroes… I begin to think they were all you.`
        : `Steel remembers, ${name}. Bring me what you found below and I'll wake it up.`,
      shop: known
        ? `I set your usual aside, ${name}. Don't ask how I knew you'd come.`
        : `Ah, ${name}. Gold means little down here — but I always have exactly what you need.`,
      home: known
        ? `The fire never went out while you were gone, ${name}. It never does.`
        : `Rest, ${name}. The road behind the house can wait; it always returns when you do.`,
    };
    return lines[type] || "";
  }
  _npcGreeting(type) { return `<div class="home-note" style="font-style:italic;color:#cdbf95;margin-bottom:10px">${this._npcLine(type)}</div>`; }

  /** Find the NPC record for a station (name + character model). */
  _stationNpc(type) { return ((this.game.level && this.game.level.npcs) || []).find((n) => n.type === type) || null; }

  /** Merchant/NPC portrait + speech bubble shown inside a station window. */
  _npcBlock(type) {
    const npc = this._stationNpc(type);
    const name = npc ? npc.name : "Merchant";
    const url = npc && npc.model && this.game.icons ? this.game.icons.renderCharacter(npc.model) : null;
    return `<div class="npc-block">
      <div class="npc-port">${url ? `<img src="${url}" alt="">` : `<span>${gi("cart")}</span>`}</div>
      <div class="npc-say"><div class="npc-name">${name}</div><p>${this._npcLine(type)}</p></div>
    </div>`;
  }

  // The Home building is one tabbed window: rest/brew, storage, crafting,
  // research, companions, feats, and the Helix — no more enter/back shuffle.
  _homeTabIds() { return ["home", "storage", "crafting", "research", "menagerie", "feats", "helix"]; }
  _homeShellHTML(view) {
    const tabs = [
      ["home", "Home"], ["storage", "Storage"], ["crafting", "Crafting"],
      ["research", "Research"], ["menagerie", "Companions"], ["feats", "Feats"], ["helix", "Helix"],
    ].map(([id, label]) => `<button class="home-tab${view === id ? " active" : ""}" data-open="${id}">${label}</button>`).join("");
    return `<div class="hub-panel home-tabbed">
      <div class="home-tabbar">
        <div class="home-tabbar-port">${this._buildingPortrait("home")}</div>
        <div class="home-tabs">${tabs}</div>
        <button class="hub-back" data-back="1" title="Close">✕</button>
      </div>
      <div class="home-tabbody">${this._homeTabBody(view)}</div>
    </div>`;
  }
  _homeTabBody(view) {
    switch (view) {
      case "home": return this._hubHomeHTML();
      case "storage": return this._hubStorageHTML();
      case "crafting": return this._craftingHTML();
      case "research": return this._hubResearchHTML();
      case "menagerie": return this._hubMenagerieHTML();
      case "feats": return this._hubFeatsHTML();
      case "helix": return this._hubHelixHTML();
      default: return "";
    }
  }

  _hubChoicesHTML() {
    return `<div class="hub-cards">
      <div class="hub-card" data-open="blacksmith"><div class="hub-glyph">${gi("anvil")}</div><div class="hub-name">Blacksmith</div><div class="hub-desc">Forge sharper weapons and imbue them with elemental enchantments.</div><div class="hub-enter">ENTER</div></div>
      <div class="hub-card" data-open="shop"><div class="hub-glyph">${gi("cart")}</div><div class="hub-name">Shop</div><div class="hub-desc">Buy powerful, enchanted and rare weapons, armour and relics.</div><div class="hub-enter">ENTER</div></div>
      <div class="hub-card" data-open="home"><div class="hub-glyph">${gi("home")}</div><div class="hub-name">Home</div><div class="hub-desc">Rest to recover your strength and craft supplies for the descent.</div><div class="hub-enter">ENTER</div></div>
    </div>
    <button class="btn hub-descend" data-descend="1">Descend to Level ${this.game.floor + 1} →</button>`;
  }

  /** A small rendered portrait of the building model (replaces the flat PNGs). */
  _buildingPortrait(type) {
    const key = { blacksmith: "hex/blacksmith", shop: "hex/shop", home: "hex/home", research: "hex/church" }[type];
    const url = key && this.game.icons ? this.game.icons.iconFor({ model: key }) : null;
    return `<div class="hub-portrait">${url ? `<img src="${url}" alt="">` : `<span>⌂</span>`}</div>`;
  }

  _stationHead(type, title) {
    return `<div class="hub-panel-head">${this._buildingPortrait(type)}<h2>${title}</h2><button class="hub-back" data-back="1">✕</button></div>`;
  }

  _hubBlacksmithHTML() {
    const p = this.game.player, floor = this.game.floor, w = p.weapon, a = p.armor, g = this.game;
    const forgeCost = g.forgeCost(), enchCost = g.enchantCost(), reinCost = g.reinforceCost(), gold = p.gold || 0;
    const cu = g.copperReserve(), bagCu = g.bagCopper(), fcu = g.forgeCopperCost(), ecu = g.enchantCopperCost();
    const dis = (c) => gold >= c ? "" : "disabled";
    // Copper "fuel" reserve stored at the forge
    const copperBar = `<div class="copper-bar">
      <span class="copper-amt">${gi("resource")} Copper fuel: <b>${cu}</b>${bagCu ? ` <span class="copper-bag">(+${bagCu} in bag)</span>` : ""}</span>
      <button class="hub-btn small" data-act="depositcopper"${bagCu ? "" : " disabled"}>Deposit copper</button>
    </div>`;
    // Forge (gated by Smithing research + a per-weapon forge cap; costs gold + copper)
    const smith = g.smithTier(), cap = g.forgeCountCap(), forges = w.forges || 0;
    const forgeBtn = smith === 0
      ? `<button class="hub-btn locked" data-open="research">${LOCK} Forge — research Apprentice Smithing</button>`
      : forges >= cap
        ? `<button class="hub-btn" disabled>Forge maxed (${forges}/${cap}) — research higher Smithing</button>`
        : `<button class="hub-btn" data-act="forge" ${(gold >= forgeCost && cu >= fcu) ? "" : "disabled"}>Forge sharper (+damage) — ${forgeCost}g + ${fcu} copper <span class="cap">${forges}/${cap}</span></button>`;
    // Enchant (gated by Arcana research; costs gold + copper)
    const enchOk = (e) => (gold >= enchCost && cu >= ecu) ? "" : "disabled";
    const elBtns = ELEMENT_LIST.map((e) => `<button class="hub-btn small" data-act="enchant" data-arg="${e}" ${enchOk(e)} style="border-color:${ELEMENTS[e].color}">${ELEMENTS[e].name}</button>`).join(" ");
    const enchSection = g.canEnchant()
      ? `<div class="forge-label">Enchant weapon — ${enchCost}g + ${ecu} copper <span class="cap">power ≤ ${g.enchantPowerCap()}</span></div><div class="forge-row">${elBtns}</div>`
      : `<button class="hub-btn locked" data-open="research">${LOCK} Enchant — research Runescribing</button>`;
    // Reinforce (gated by Armoursmithing; gold only)
    const reinBtn = g.canReinforce()
      ? `<button class="hub-btn" data-act="reinforce" ${dis(reinCost)}>Reinforce armour (+def, +hp) — ${reinCost}g</button>`
      : `<button class="hub-btn locked" data-open="research">${LOCK} Reinforce — research Armoursmithing</button>`;
    return `<div class="hub-panel">
      ${this._stationHead("blacksmith", "Blacksmith")}
      ${this._npcBlock("blacksmith")}
      ${copperBar}
      ${this._gearCard(w, `${forgeBtn}${enchSection}`)}
      ${this._gearCard(a, reinBtn)}
      <button class="hub-btn ghost" data-open="research">◆ Research tree</button>
    </div>`;
  }

  /** A horizontal gear card (thumbnail + name + stats) with action buttons beneath. */
  _gearCard(it, actions) {
    const rc = it.rarity ? it.rarity.color : "#cccccc";
    const icon = this._icon(it);
    const vis = icon ? `<img src="${icon}" alt="" draggable="false">` : `<span class="glyph">${itemGlyph(it)}</span>`;
    const type = it.kind === "weapon" ? (it.wtype === "ranged" ? (it.magic ? "Magic" : "Ranged") : "Melee") : "Armour";
    return `<div class="gear-card" style="--rc:${rc}">
      <div class="gear-thumb">${vis}</div>
      <div class="gear-info">
        <div class="gear-name" style="color:${rc}">${it.named ? "✦ " : ""}${it.name}</div>
        <div class="gear-meta">${it.rarity ? it.rarity.name : ""} · ${type}</div>
        <div class="gear-stats">${this._shopStats(it)}</div>
        <div class="gear-actions">${actions}</div>
      </div>
    </div>`;
  }

  /** Live companion widget shown during play (name, level, XP, current effect). */
  _renderPets() {
    const g = this.game, el = this.el.pets; if (!el) return;
    const list = [];
    if (g._wisp) list.push({ inst: g._wisp, id: "wisp" });
    if (g._activePet) list.push({ inst: g._activePet, id: g.meta.activePet });
    if (g.state !== "playing" || !list.length) { el.style.display = "none"; return; }
    const sig = list.map(({ id }) => { const s = g.save.petState(id); return id + s.level + Math.round(s.xp); }).join("|") + document.documentElement.getAttribute("data-ui");
    if (sig === this._petSig) return;   // only rebuild when something changed
    this._petSig = sig;
    el.style.display = "flex";
    el.innerHTML = list.map(({ inst, id }) => {
      const def = inst.def, st = g.save.petState(id);
      const need = g.save.petXpNeeded(st.level);
      const pct = Math.min(100, Math.round((st.xp / need) * 100));
      return `<div class="petpill">
        <div class="pp-port">${this._petPortrait(def)}</div>
        <div class="pp-body">
          <div class="pp-top"><b>${def.name}</b><span>Lv ${st.level}</span></div>
          <div class="pp-eff">${this._petEffect(def, st.level)}</div>
          <div class="pp-xp"><span style="width:${pct}%"></span></div>
        </div>
      </div>`;
    }).join("");
  }

  /** Human-readable effect of a pet at a given level. */
  _petEffect(def, level) {
    if (def.role === "collector") return `Auto-collects loot within ${(4.6 + level * 0.4).toFixed(1)}m`;
    if (def.role === "hunter" && def.hunter) { const h = def.hunter(level); return `Fires bolts — ${h.dmg} dmg every ${h.cd.toFixed(1)}s`; }
    if (def.grants) {
      const gr = def.grants(level), parts = [];
      if (gr.hp) parts.push(`+${Math.round(gr.hp)} max HP`);
      if (gr.mana) parts.push(`+${Math.round(gr.mana)} max mana`);
      if (gr.manaRegen) parts.push(`+${gr.manaRegen.toFixed(1)} mana/s`);
      if (gr.crit) parts.push(`+${Math.round(gr.crit * 100)}% crit`);
      if (gr.luck) parts.push(`+${Math.round(gr.luck * 100)}% loot fortune`);
      if (gr.dmg) parts.push(`+${Math.round(gr.dmg * 100)}% damage`);
      return parts.join(", ");
    }
    return def.desc;
  }

  /** Rendered portrait of a companion (creature model, or a glowing orb for the Wisp). */
  _petPortrait(def) {
    if (def.model && this.game.icons) {
      const url = this.game.icons.renderCharacter(def.model);
      if (url) return `<img src="${url}" alt="" draggable="false">`;
    }
    const tint = "#" + ((def.tint || 0xc77dff) >>> 0).toString(16).padStart(6, "0");
    return `<span class="pet-orb" style="--tint:${tint}"></span>`;
  }

  _petCardHTML(p, footBtn) {
    const st = this.game.save.petState(p.id);
    const need = this.game.save.petXpNeeded(st.level);
    const pct = st.unlocked ? Math.min(100, Math.round((st.xp / need) * 100)) : 0;
    const eff = st.unlocked ? this._petEffect(p, st.level) : `Tame by reaching depth ${p.unlockDepth}`;
    return `<div class="petcard ${footBtn.cls}">
      <div class="pet-thumb${st.unlocked ? "" : " locked"}">${this._petPortrait(p)}</div>
      <div class="pet-head"><span class="pet-ico">${roleGlyph(p.role)}</span><b>${p.name}</b>${st.unlocked ? `<span class="pet-lvl">Lv ${st.level}</span>` : ""}</div>
      <div class="pet-role">${p.role}</div>
      <div class="pet-desc">${p.desc}</div>
      <div class="pet-eff">${eff}</div>
      ${st.unlocked ? `<div class="mem-bar"><span style="width:${pct}%;background:#7bd645"></span></div>` : ""}
      ${footBtn.html}
    </div>`;
  }

  /** One compact storage row for the stack `it` at index `i`. side "bag" → store, "stash" → take. */
  _stashRow(it, i, side) {
    const n = stackCount(it);
    const rc = it.rarity ? it.rarity.color : "#c8c8c8";
    const icon = this._icon(it);
    const vis = icon ? `<img src="${icon}" alt="" draggable="false">` : `<span class="glyph">${itemGlyph(it)}</span>`;
    const whole = side === "bag" ? "store" : "take";
    const single = side === "bag" ? "storeone" : "takeone";
    const full = side === "stash" && !canAdd(this.game.player.inv, it, this.game.player.invCap);
    const dis = full ? " disabled" : "";
    const wholeLbl = side === "bag" ? (n > 1 ? `▸ all` : "▸") : (n > 1 ? `all ◂` : "◂");
    const cnt = n > 1 ? `<span class="st-count">×${n}</span>` : "";
    return `<div class="st-row" style="--rc:${rc}" data-smact="${whole}" data-sarg="${i}" title="Shift-click to move the whole stack">
      <div class="st-ico">${vis}</div>
      <div class="st-info">
        <div class="st-name" style="color:${rc}">${it.named ? "✦ " : ""}${it.name}${cnt}</div>
        <div class="st-meta">${this._shopStats(it) || (it.kind.charAt(0).toUpperCase() + it.kind.slice(1))}</div>
      </div>
      <div class="st-btns">
        ${n > 1 ? `<button class="st-move one" data-act="${single}" data-arg="${i}"${dis} title="Move one">${side === "bag" ? "▸1" : "1◂"}</button>` : ""}
        <button class="st-move" data-act="${whole}" data-arg="${i}"${dis} title="${n > 1 ? "Move whole stack" : "Move"}">${wholeLbl}</button>
      </div>
    </div>`;
  }

  _hubStorageHTML() {
    const p = this.game.player;
    const stash = (this.game.meta && this.game.meta.stash) || [];
    const bagRows = p.inv.map((it, i) => (it && it.kind !== "coins") ? this._stashRow(it, i, "bag") : "").join("");
    const stashRows = stash.map((it, i) => this._stashRow(it, i, "stash")).join("");
    const anyBag = p.inv.some((it) => it && it.kind !== "coins");
    const full = slotCount(p.inv) >= p.invCap;
    return `<div class="hub-panel storage-panel">
      <div class="home-note st-note">Deposit loot between runs — it stays safe even if you fall. Stacks combine automatically.</div>
      <div class="st-grid">
        <div class="st-col">
          <div class="st-colhead"><b>Your Bag</b><span class="st-dim">${slotCount(p.inv)}/${p.invCap} slots</span>
            ${anyBag ? `<button class="st-bulk" data-act="storeall">Store all ▸</button>` : ""}</div>
          <div class="st-list">${bagRows || `<div class="storage-empty">Your bag is empty.</div>`}</div>
        </div>
        <div class="st-col">
          <div class="st-colhead"><b>Home Storage</b><span class="st-dim">${stash.length} stacks · ∞</span>
            ${stash.length ? `<button class="st-bulk" data-act="takeall"${full ? " disabled" : ""}>◂ Take all</button>` : ""}</div>
          <div class="st-list">${stashRows || `<div class="storage-empty">Empty. Deposit loot — home storage is unlimited.</div>`}</div>
        </div>
      </div>
      ${(() => { const left = this.game.bagExpansionsLeft(); const mem = totalCollected(this.game.meta.codex || {});
        return `<button class="hub-btn" data-act="expandbag" ${left > 0 && mem > 0 ? "" : "disabled"}>◆ Expand your Bag +1 slot — trade 1 memory ${left > 0 ? `(${left} left)` : "(maxed)"}</button>`; })()}
    </div>`;
  }

  _craftingHTML() {
    const g = this.game, p = g.player;
    const cu = g.copperReserve(), gold = p.gold || 0;
    const ic = g.icons;
    const copperIco = ic ? ic.iconFor({ model: "resource/copper_bar" }) : null;
    const goldIco = ic ? ic.iconFor({ model: "dungeon/coins" }) : null;
    // rendered-thumbnail helper (falls back to the theme glyph only if a render fails)
    const thumb = (url, alt, fallback) => url
      ? `<img class="craft-img" src="${url}" alt="${alt}" title="${alt}" draggable="false">`
      : `<span class="craft-img fb">${fallback || ""}</span>`;
    const ing = (url, amt, label, ok) =>
      `<span class="ing${ok ? "" : " poor"}" title="${label}">${thumb(url, label, "")}<b>${amt}</b> ${label}</span>`;

    const rows = g.recipes().map((r) => {
      const owned = !r.req || g.hasResearch(r.req);
      const canDo = g.canCraft(r);
      const outIco = ic ? ic.iconFor({ model: r.model }) : null;
      return `<div class="craft-row${canDo ? "" : " off"}">
        <div class="craft-thumb">${thumb(outIco, r.name, gi("potion"))}</div>
        <div class="craft-info">
          <div class="craft-name">${r.name}${owned ? "" : ` <span class="craft-lock">needs research</span>`}</div>
          <div class="craft-desc">${r.desc}</div>
          <div class="craft-cost">${ing(copperIco, r.copper, "Copper", cu >= r.copper)}${ing(goldIco, r.gold, "Gold", gold >= r.gold)}</div>
        </div>
        <button class="craft-btn" data-act="craft" data-arg="${r.id}"${canDo ? "" : " disabled"}>Craft</button>
      </div>`;
    }).join("");

    return `<div class="craft-panel">
      <div class="st-colhead"><b>Crafting</b><span class="st-dim">You hold ${thumb(copperIco, "Copper", "")} ${cu} copper · ${thumb(goldIco, "Gold", "")} ${gold} gold</span></div>
      <div class="craft-list">${rows}</div>
      <div class="home-note st-note">Copper fuel is stored at the blacksmith — deposit copper there to stock it. Each recipe spends copper + gold.</div>
    </div>`;
  }

  _hubHelixHTML() {
    const p = this.game.player;
    const hx = this.game.helixState();
    const collected = (this.game.meta && this.game.meta.codex) || {};
    const carried = p.inv.reduce((n, it) => n + (it && it.kind === "fragment" ? stackCount(it) : 0), 0);
    const pct = hx.total ? Math.round((hx.sections / hx.total) * 100) : 0;
    const libPct = hx.library ? Math.round((hx.collected / hx.library) * 100) : 0;
    // 100 codex cells — a cell lights only when its whole codex is collected
    let cells = "";
    for (let c = 1; c <= hx.total; c++) {
      const cx = getCodex(c);
      const prog = codexProgress(c, collected);
      const done = prog.total > 0 && prog.have >= prog.total;
      const started = prog.have > 0 && !done;
      const sel = this._helixCodex === c;
      cells += `<div class="hx-cell${done ? " lit" : ""}${started ? " part" : ""}${sel ? " sel" : ""}" style="${done ? `--c:${cx.color}` : ""}" data-codex="${c}" title="Codex ${c}: ${cx.title} · ${prog.have}/${prog.total}"></div>`;
    }
    // selected codex reader (defaults to the first incomplete one)
    let sel = this._helixCodex;
    if (!sel) { for (let c = 1; c <= hx.total; c++) { if (!codexComplete(c, collected)) { sel = c; break; } } sel = sel || 1; }
    const cx = getCodex(sel), prog = codexProgress(sel, collected);
    const mems = cx.memories.map((m) => collected[m.id]
      ? `<div class="codex-entry"><h4>${m.title}</h4><p>${m.text}</p></div>`
      : `<div class="codex-entry locked"><h4>???</h4><p>An unrecovered memory — find it in the depths.</p></div>`
    ).join("");
    return `<div class="hub-panel helix-panel">
      <div class="hx-head"><h2>✦ The Helix of Restoration</h2>
        <div class="hx-sub">Every memory you recover in the depths is offered <b>here</b>. Each <b>codex</b> is one line of the Helix — collect <b>all</b> of its memories and that line ignites, forging a <b>Legendary Memory Relic</b> found nowhere else. Fill all ${hx.total} codices and the broken world is restored.</div></div>
      <div class="hx-stat">
        <div class="hx-big">${hx.sections}<span>/${hx.total}</span></div>
        <div class="hx-meta">
          <div class="hx-line">Codices restored — ${pct}%</div>
          <div class="hx-barwrap"><span class="hx-bar" style="width:${pct}%"></span></div>
          ${hx.restored ? `<div class="hx-done">THE WORLD IS RESTORED ✦</div>` : ""}
          <div class="hx-line">Memories recovered — ${hx.collected} / ${hx.library} (${libPct}%)</div>
          <div class="hx-barwrap sm"><span class="hx-bar next" style="width:${libPct}%"></span></div>
          <div class="hx-lore">Memory Relics forged: <b>${hx.sections}</b>${carried ? ` · ${carried} in your bag to offer` : ""}</div>
        </div>
      </div>
      <button class="hub-btn hx-turnin" data-act="turnin" ${carried ? "" : "disabled"}>${carried ? `✦ Offer ${carried} ${carried === 1 ? "Memory" : "Memories"} to the Helix` : "No memories to offer — recover more in the depths"}</button>
      <div class="hx-grid">${cells}</div>
      <div class="hx-reader">
        <div class="hx-reader-head"><b style="color:${prog.have >= prog.total && prog.total ? cx.color : "#cfc6e6"}">Codex ${sel} — ${cx.title}</b>
          <span class="st-dim">${cx.tierName} · ${prog.have}/${prog.total} memories</span></div>
        <div class="codex-list">${mems}</div>
      </div>
    </div>`;
  }

  _hubMenagerieHTML() {
    const g = this.game, m = g.meta, deepest = m.deepest || 0;
    // --- utility slot: the Wisp ---
    const wisp = PETS.find((p) => p.utility);
    const wSt = g.save.petState(wisp.id);
    const wOut = wSt.unlocked && !g.wispDismissed;
    const wBtn = !wSt.unlocked
      ? { cls: "locked", html: `<button class="hub-btn small" disabled>${LOCK} reach depth ${wisp.unlockDepth}</button>` }
      : { cls: wOut ? "active" : "", html: `<button class="hub-btn small" data-act="wisp">${wOut ? "Dismiss [P]" : "Summon [P]"}</button>` };
    // --- creature slot: pick one ---
    const creatures = PETS.filter((p) => !p.utility);
    const active = m.activePet;
    const cCards = creatures.map((p) => {
      const st = g.save.petState(p.id);
      const isActive = active === p.id;
      const btn = !st.unlocked
        ? { cls: "locked", html: `<button class="hub-btn small" disabled>${LOCK} reach depth ${p.unlockDepth}</button>` }
        : isActive
          ? { cls: "active", html: `<button class="hub-btn small" data-act="petoff">● Active — dismiss</button>` }
          : { cls: "", html: `<button class="hub-btn small" data-act="pet" data-arg="${p.id}">Summon</button>` };
      return this._petCardHTML(p, btn);
    }).join("");
    return `<div class="hub-panel">
      <div class="home-note" style="font-style:italic">Two companions travel with you — the utility Wisp (toggle with <b>[P]</b>) and one creature ally. They orbit opposite sides and grow stronger as you fight. Deepest: ${deepest}.</div>
      <div class="forge-label">Utility — Wisp</div>
      <div class="petgrid">${this._petCardHTML(wisp, wBtn)}</div>
      <div class="forge-label">Creature companion (choose one)</div>
      <div class="petgrid">${cCards}</div>
    </div>`;
  }

  /** Feats view at the Waystation Home — the CURRENTLY PLAYED hero's achievements. */
  _hubFeatsHTML() {
    const ch = this.game.character;
    const prof = this.game.profile || (this.game.save && ch ? this.game.save.profile(ch.id) : {});
    if (!ch) return `<div class="hub-panel"><div class="home-note">No hero selected.</div></div>`;
    const heroId = ch.id;
    return `<div class="hub-panel feats-panel">
      <div class="home-note" style="font-style:italic">${ch.name}'s deeds. Hero feats (marked <b>HERO</b>) unlock this hero's skins.</div>
      ${this._featBodyHTML(heroId, prof)}
    </div>`;
  }

  _hubResearchHTML() {
    const g = this.game, m = g.meta;
    const insight = Math.floor(m.insight || 0), deepest = m.deepest || 0;
    // The tree layout + SVG is the heaviest hub render; cache it and only rebuild
    // when research state actually changes (unlock / insight / depth), so unrelated
    // hub actions (crafting, deposits, tab switches) reuse the computed markup.
    const owned = Object.keys(m.research || {}).filter((k) => m.research[k]).sort().join(",");
    const sig = `${deepest}|${insight}|${owned}`;
    if (this._researchCache && this._researchCache.sig === sig) return this._researchCache.html;
    const branchIco = { Smithing: "⚒", Arcana: "✦", Warfare: "⚔", Survival: "☘", Lore: "❖", Fortune: "◆", Prowess: "★" };
    const mods = g.researchMods();
    const chips = [];
    if (mods.dmg) chips.push(`+${Math.round(mods.dmg * 100)}% dmg`);
    if (mods.crit) chips.push(`+${Math.round(mods.crit * 100)}% crit`);
    if (mods.hp) chips.push(`+${mods.hp} HP`);
    if (mods.stam) chips.push(`+${mods.stam} stam`);
    if (mods.mana) chips.push(`+${mods.mana} mana`);
    if (mods.speed) chips.push(`+${Math.round(mods.speed * 100)}% speed`);
    if (mods.insight) chips.push(`+${Math.round(mods.insight * 100)}% insight`);
    if (mods.luck) chips.push(`+${Math.round(mods.luck * 100)}% luck`);
    const modLine = chips.length ? `<div class="research-mods">Active bonuses · ${chips.join(" · ")}</div>` : "";

    // ---- layout: branch = column, tier = row; siblings share a tier as lanes ----
    const NODE_W = 150, NODE_H = 106, LANE = 168, ROWH = 138, PADX = 20, PADY = 46, GAP = 26;
    const cols = BRANCHES.map((br) => {
      const nodes = RESEARCH.filter((n) => n.branch === br);
      const byTier = {};
      nodes.forEach((n) => { (byTier[n.tier] = byTier[n.tier] || []).push(n); });
      const lanes = Math.max(1, ...Object.values(byTier).map((a) => a.length));
      return { br, nodes, byTier, lanes };
    });
    const pos = {}; let x0 = PADX, maxTier = 1;
    for (const col of cols) {
      const center = x0 + (col.lanes * LANE) / 2;
      for (const t in col.byTier) {
        const tier = +t; maxTier = Math.max(maxTier, tier);
        const arr = col.byTier[t];
        arr.forEach((n, i) => {
          const off = (i - (arr.length - 1) / 2) * LANE;
          pos[n.id] = { x: Math.round(center + off - NODE_W / 2), y: PADY + (tier - 1) * ROWH };
        });
      }
      col.x = x0; col.center = center; x0 += col.lanes * LANE + GAP;
    }
    const totalW = Math.round(x0 - GAP + PADX), totalH = PADY + (maxTier - 1) * ROWH + NODE_H + 16;

    // ---- connectors (prereq bottom → node top), colour by whether prereq owned ----
    let paths = "";
    for (const n of RESEARCH) {
      for (const r of (n.requires || [])) {
        const a = pos[r], b = pos[n.id]; if (!a || !b) continue;
        const ax = a.x + NODE_W / 2, ay = a.y + NODE_H, bx = b.x + NODE_W / 2, by = b.y;
        const midY = (ay + by) / 2;
        const on = !!m.research[r];
        paths += `<path d="M${ax},${ay} C${ax},${midY} ${bx},${midY} ${bx},${by}" class="rwire ${on ? "on" : ""}"/>`;
      }
    }

    // ---- branch headers + nodes ----
    let heads = "";
    for (const col of cols) heads += `<div class="rbranch" style="left:${Math.round(col.center - LANE / 2)}px;width:${col.lanes * LANE}px">${branchIco[col.br] || ""} ${col.br}</div>`;
    let nodesHtml = "";
    for (const n of RESEARCH) {
      const p = pos[n.id]; const st = g.researchStatus(n);
      const badge = st === "owned" ? `<span class="rn-badge ok">✓</span>`
        : st === "available" ? `<span class="rn-badge go">◆</span>`
          : `<span class="rn-badge">${LOCK}</span>`;
      const foot = st === "owned" ? `<span class="rn-owned">Researched</span>`
        : st === "available" ? `<button class="hub-btn small" data-act="research" data-arg="${n.id}">Unlock · ${n.cost} ◆</button>`
          : st === "poor" ? `<button class="hub-btn small" disabled>Costs ${n.cost} ◆</button>`
            : `<button class="hub-btn small" disabled>Reach depth ${n.reqDepth}</button>`;
      nodesHtml += `<div class="rnode ${st}" style="left:${p.x}px;top:${p.y}px;width:${NODE_W}px">
        <div class="rn-head">${badge}<b>${n.name}</b><span class="rn-tier">T${n.tier}</span></div>
        <div class="rn-desc">${n.desc}</div>${foot}</div>`;
    }

    const html = `<div class="hub-panel research">
      <div class="research-top"><span class="insight-badge">◆ ${insight} Insight</span><span class="research-sub">Deepest reached: ${deepest} · Insight comes from recovering memories, surviving new depths & slaying the Forgotten</span></div>
      ${modLine}
      <div class="rtree-scroll"><div class="rtree" style="width:${totalW}px;height:${totalH}px">
        <svg class="rtree-svg" width="${totalW}" height="${totalH}" viewBox="0 0 ${totalW} ${totalH}">${paths}</svg>
        ${heads}${nodesHtml}
      </div></div>
    </div>`;
    this._researchCache = { sig, html };
    return html;
  }

  /** Compact stat line for a shop card. */
  _shopStats(it) {
    if (it.kind === "weapon") {
      const e = it.element ? ` · <span style="color:${ELEMENTS[it.element] ? ELEMENTS[it.element].color : "#fff"}">${ELEMENTS[it.element] ? ELEMENTS[it.element].name : it.element}</span>` : "";
      return `${it.dmgMin}–${it.dmgMax} dmg${e}`;
    }
    if (it.kind === "armor") return `+${it.def} def · +${it.hp} hp`;
    if (it.kind === "potion") return (it.potType === "mana" || it.mana) ? `+${it.mana} MP` : `+${it.heal} HP`;
    if (it.kind === "food") return `heals ${it.heal || 0}`;
    if (it.kind === "treasure") return `worth ${it.value}g`;
    if (it.kind === "resource") return "material";
    return "";
  }

  /** A framed shop card with a rendered item thumbnail, rarity glow + a buy/sell button. */
  _shopCard(it, { idx, action, label, disabled = false, sold = false }) {
    const rc = it.rarity ? it.rarity.color : "#cccccc";
    const icon = this._icon(it);
    const vis = icon ? `<img src="${icon}" alt="" draggable="false">` : `<span class="glyph">${itemGlyph(it)}</span>`;
    const type = it.kind === "weapon" ? (it.wtype === "ranged" ? (it.magic ? "Magic" : "Ranged") : "Melee")
      : it.kind.charAt(0).toUpperCase() + it.kind.slice(1);
    return `<div class="shop-card${sold ? " sold" : ""}" style="--rc:${rc}">
      <div class="shop-thumb">${vis}</div>
      <div class="shop-cname" style="color:${rc}">${it.named ? "✦ " : ""}${it.name}</div>
      <div class="shop-cmeta">${it.rarity ? it.rarity.name : ""} · ${type}</div>
      <div class="shop-cstats">${this._shopStats(it)}</div>
      <button class="shop-buy" data-act="${action}" data-arg="${idx}"${disabled ? " disabled" : ""}>${label}</button>
    </div>`;
  }

  _hubShopHTML() {
    const p = this.game.player, gold = p.gold || 0, floor = this.game.floor;
    const buy = (this.game.shopStock || []).map((s, i) => {
      const canBuy = !s.sold && gold >= s.price && canAdd(p.inv, s.item, p.invCap);
      return this._shopCard(s.item, { idx: i, action: "buy", label: s.sold ? "SOLD" : `${gi("coins")} ${s.price}`, disabled: !canBuy, sold: s.sold });
    }).join("");
    const sell = p.inv.map((it, i) => ({ it, i })).filter(({ it }) => it && isSellable(it))
      .map(({ it, i }) => {
        const q = stackCount(it);
        return this._shopCard(it, { idx: i, action: "sell", label: q > 1 ? `Sell ${q} · ${sellPrice(it, floor) * q}` : `Sell ${sellPrice(it, floor)}` });
      }).join("");
    const keyPrice = this.game.keyPrice();
    const keyBtn = `<div class="forge-label">Vault Keys — you hold 🗝 ${p.keys || 0}</div>
      <div class="shop-shelf"><button class="hub-btn" data-act="buykey" ${gold >= keyPrice ? "" : "disabled"}>Buy a Vault Key — ${gi("coins")} ${keyPrice}</button></div>`;
    return `<div class="hub-panel shop">
      ${this._stationHead("shop", "Shop")}
      ${this._npcBlock("shop")}
      <div class="forge-label">Wares for sale — you carry ${gi("coins")} ${gold}g</div>
      <div class="shop-shelf">${buy || '<div class="home-note">The merchant is sold out.</div>'}</div>
      ${keyBtn}
      <div class="forge-label">Sell from your bag</div>
      <div class="shop-shelf">${sell || '<div class="home-note">Nothing here to sell.</div>'}</div>
      <div class="forge-label">Redeem a code</div>
      <div class="redeem-row">
        <input id="redeemInput" class="redeem-input" type="text" placeholder="Paste your code…" maxlength="48" autocomplete="off" spellcheck="false">
        <button class="hub-btn" data-act="redeem">Claim</button>
      </div>
    </div>`;
  }

  _hubHomeHTML() {
    const p = this.game.player, rested = this.game.homeRested, brewCost = 35, gold = p.gold || 0;
    const carried = p.inv.reduce((n, i) => n + (i && i.kind === "fragment" ? stackCount(i) : 0), 0);
    const insight = Math.floor((this.game.meta && this.game.meta.insight) || 0);
    const brewBtn = this.game.canBrew()
      ? `<button class="hub-btn" data-act="brew" ${gold >= brewCost && (slotCount(p.inv) < p.invCap || p.inv.some((x) => x && x.kind === "potion")) ? "" : "disabled"}>Brew ${this.game.brewGreater() ? "Greater " : ""}Potion — ${brewCost}g</button>`
      : `<button class="hub-btn locked" data-open="research">${LOCK} Brew Potion — research Provisioning</button>`;
    const hx = this.game.helixState();
    return `<div class="hub-panel home-body">
      ${this._npcBlock("home")}
      <div class="forge-label">Recovery</div>
      <div class="forge-item">Health: ${Math.ceil(p.hp)} / ${p.maxHp}</div>
      <button class="hub-btn" data-act="rest" ${rested ? "disabled" : ""}>${rested ? "Rested" : "Rest — restore full health (free)"}</button>
      ${brewBtn}
      <div class="forge-label">At a glance</div>
      <div class="home-glance">
        <button class="hub-glance-card" data-open="helix"><span class="gc-k">Helix restored</span><span class="gc-v">${hx.sections}/${hx.total}</span>${carried ? `<span class="gc-note">${carried} ${carried === 1 ? "memory" : "memories"} to offer →</span>` : ""}</button>
        <button class="hub-glance-card" data-open="research"><span class="gc-k">Insight</span><span class="gc-v">${insight} ◆</span><span class="gc-note">spend on research →</span></button>
      </div>
    </div>`;
  }

  /** Character-select screen — build a card per hero, click to begin the run. */
  showCharacterSelect() {
    const scr = this.el.character;
    if (!scr) { this.game.start(); return; }
    this.el.start.classList.add("hidden");
    this.el.death.classList.add("hidden");
    // hide the in-game HUD (we may be arriving here from a death mid-run)
    this.el.hud.classList.add("hidden");
    this.el.hotbar.classList.add("hidden");
    this.el.minimap?.classList.add("hidden");
    this.el.controls?.classList.add("hidden");
    this.el.intermission?.classList.add("hidden");
    this.el.prompt?.classList.add("hidden");
    this.el.inventory?.classList.add("hidden");
    if (this.el.pets) this.el.pets.style.display = "none";
    scr.classList.remove("hidden");
    this._csIndex = 0;
    this.game.enterCharSelect(CHARACTERS[0].id);   // live 3D turntable in the scene
    this._renderCharSelect();
  }

  _csGo(i) {
    this._csIndex = (i + CHARACTERS.length) % CHARACTERS.length;
    this.game.setPreviewChar(CHARACTERS[this._csIndex].id);
    this._renderCharSelect();
  }

  _renderCharSelect() {
    const scr = this.el.character;
    const c = CHARACTERS[this._csIndex];
    const prof = this.game.save ? this.game.save.profile(c.id) : {};
    const lvl = prof.level || 1;
    const earned = earnedCount(prof, c.id), total = achievementCount(c.id);
    const featsBlock =
      `<button class="btn cs-feats-btn" id="csFeatsBtn">★ View Feats · ${earned}/${total}</button>`;
    const strip = CHARACTERS.map((x, i) => {
      const l = this.game.save ? this.game.save.profile(x.id).level : 1;
      const portrait = this.game.icons ? this.game.icons.renderCharacter(x.model, starterKit(x.id).weapon.model) : null;
      const vis = portrait ? `<img src="${portrait}" alt="">` : `<span class="cs-thumb-glyph">${x.glyph}</span>`;
      return `<div class="cs-thumb${i === this._csIndex ? " active" : ""}" data-i="${i}" style="--accent:${x.color}">${vis}<span class="cs-thumb-lvl">Lv ${l}</span></div>`;
    }).join("");
    scr.innerHTML =
      `<h1 class="cs-title">CHOOSE YOUR HERO</h1>` +
      `<button class="btn btn-back cs-back" id="charBackBtn">← Back</button>` +
      `<div class="cs-panel" style="--accent:${c.color}">` +
        `<div class="cs-name">${c.name} <span class="char-lvl">Lv ${lvl}</span></div>` +
        `<div class="cs-role">${c.glyph} ${c.title}</div>` +
        `<div class="cs-lore">${c.lore}</div>` +
        `<div class="cs-gear">${c.gear}</div>` +
        `<input id="heroName" class="cs-name-input" maxlength="18" placeholder="Name your hero…" value="${(this.game.save ? (this.game.save.profile(c.id).name || "") : "").replace(/"/g, "&quot;")}" />` +
        featsBlock +
        `<button class="btn cs-choose" id="csChoose">CHOOSE</button>` +
      `</div>` +
      `<div class="cs-strip"><button class="cs-arrow" id="csPrev">‹</button><div class="cs-thumbs">${strip}</div><button class="cs-arrow" id="csNext">›</button></div>`;
    scr.querySelector("#charBackBtn").onclick = () => { this.game.exitCharSelect(); scr.classList.add("hidden"); this.el.start.classList.remove("hidden"); };
    scr.querySelector("#csChoose").onclick = () => {
      const input = scr.querySelector("#heroName");
      const nm = (input ? input.value : "").trim().slice(0, 18);
      if (this.game.save) { const prof = this.game.save.profile(c.id); prof.name = nm || c.name; this.game.save.write(); }
      scr.classList.add("hidden"); this.game.begin(c.id);
    };
    scr.querySelector("#csPrev").onclick = () => this._csGo(this._csIndex - 1);
    scr.querySelector("#csNext").onclick = () => this._csGo(this._csIndex + 1);
    const ft = scr.querySelector("#csFeatsBtn");
    if (ft) ft.onclick = () => this.openFeatsModal(c.id);
    scr.querySelectorAll(".cs-thumb").forEach((el) => el.onclick = () => this._csGo(+el.dataset.i));
  }

  /**
   * Shared per-hero feats UI (summary + skin rewards + card grid). Reused by the
   * character-select Feats MODAL and the Waystation Home "Feats" panel so both
   * share one look. Each card has an art slot (featArt) ready for real artwork.
   */
  _featSummaryHTML(heroId, prof) {
    const earned = earnedCount(prof, heroId), total = achievementCount(heroId);
    const heroTotal = heroAchievements(heroId).length, heroDone = heroEarnedCount(prof, heroId);
    const mastered = earned >= total;
    return `<div class="feat-summary"><b>${earned}/${total}</b> feats earned` +
      `${mastered ? " · <span class='feat-mastered'>✦ MASTERED</span>" : ""} · ${heroDone}/${heroTotal} hero feats</div>`;
  }
  /** Interactive skin picker — colour swatches; locked ones show their feat req on hover. */
  _skinPicksHTML(heroId, prof) {
    const active = prof.skin || "default";
    return `<div class="skin-picks">${[...SKINS, PRESTIGE_SKIN].map((s) => {
      const on = skinUnlocked(s, prof, heroId);
      const dot = s.tint == null
        ? "background:linear-gradient(135deg,#d7cdea,#8a80a8)"
        : `background:#${s.tint.toString(16).padStart(6, "0")}`;
      const title = on ? s.name : `${s.name} — ${skinRequirement(s, heroId)}`;
      return `<button class="skin-pick${active === s.id ? " active" : ""}${on ? "" : " locked"}" data-skin="${s.id}" title="${title}" ${on ? "" : "disabled"}>` +
        `<span class="skin-dot" style="${dot}"></span><span class="skin-lbl">${s.name}</span>` +
        `${on ? "" : `<span class="skin-lock">${LOCK}</span>`}</button>`;
    }).join("")}</div>`;
  }
  _featCardsHTML(heroId, prof) {
    return `<div class="feat-grid">` + achievementsFor(heroId).map((a) => {
      const pr = achieveProgress(prof, a);
      const tag = a.hero ? `<span class="ach-tag">HERO</span>` : "";
      return `<div class="feat-card${pr.done ? " done" : " locked"}">
        <div class="feat-art">${featArt(a)}<span class="feat-badge">${pr.done ? "★" : "☆"}</span></div>
        <div class="feat-name">${a.title}${tag}</div>
        <div class="feat-desc">${a.desc}</div>
        <div class="feat-prog">${Math.min(pr.have, pr.need)}/${pr.need}</div>
        ${pr.done && FEATURES.sharing ? `<button class="feat-share" data-share-feat="${a.id}" data-share-hero="${heroId}" title="Share this feat">↗ Share</button>` : ""}
      </div>`;
    }).join("") + `</div>`;
  }
  /** Full feats body: summary + skin picker + card grid (shared modal/waystation). */
  _featBodyHTML(heroId, prof) {
    return this._featSummaryHTML(heroId, prof) +
      `<div class="feat-sec-label">Skins <span>tap to equip</span></div>` +
      this._skinPicksHTML(heroId, prof) +
      `<div class="feat-sec-label">Feats</div>` +
      this._featCardsHTML(heroId, prof);
  }

  /** Open the shared Feats modal for a given hero (from character select). */
  openFeatsModal(heroId) {
    const m = document.getElementById("featsModal"); if (!m) return;
    const c = CHARACTERS.find((x) => x.id === heroId) || {};
    const ttl = document.getElementById("featsModalTitle");
    const body = document.getElementById("featsModalBody");
    if (ttl) ttl.innerHTML = `${c.glyph || "★"} ${c.name || "Hero"} · Feats`;
    const draw = () => {
      const prof = this.game.save ? this.game.save.profile(heroId) : {};
      if (!body) return;
      body.innerHTML = this._featBodyHTML(heroId, prof);
      body.querySelectorAll("[data-skin]").forEach((el) => el.onclick = () => {
        if (!this.game.save) return;
        this.game.save.profile(heroId).skin = el.dataset.skin; this.game.save.write();
        // live-apply if this is the hero currently in play
        if (this.game.character && this.game.character.id === heroId) this.game.setSkin(el.dataset.skin);
        draw();
      });
    };
    draw();
    // ESC + backdrop-click close are handled globally in main.js; wire the button.
    const btn = document.getElementById("featsModalClose");
    if (btn) btn.onclick = () => m.classList.add("hidden");
    m.classList.remove("hidden");
  }
  showDeath(floor, kills) {
    this._lastRun = { depth: floor, kills, fell: true };   // for the Share button
    if (this.el.deathStats) this.el.deathStats.textContent = `You reached depth ${floor} and slew ${kills} foes.`;
    this.el.death.classList.remove("hidden");
    this.el.hud.classList.add("hidden"); this.el.hotbar.classList.add("hidden"); this.el.minimap?.classList.add("hidden");
    this.el.controls?.classList.add("hidden"); this.el.intermission?.classList.add("hidden"); this.el.prompt?.classList.add("hidden");
    this.el.inventory?.classList.add("hidden");
    if (this.el.pets) this.el.pets.style.display = "none";
  }

  showDeathModal({ have, cost, onRevive, onNewRun }) {
    const scr = document.getElementById("reviveScreen");
    if (!scr) { onNewRun(); return; }
    this._lastRun = { depth: this.game.floor, kills: this.game.totalKills, fell: true };   // for the Share button
    const txt = document.getElementById("reviveText");
    const yes = document.getElementById("reviveYes"), no = document.getElementById("reviveNo");
    if (onRevive) {
      const w = cost === 1 ? "memory" : "memories";
      const hw = have === 1 ? "memory" : "memories";
      if (txt) txt.textContent = `You have ${have} recovered ${hw}. Trade ${cost} back to the Vault to rise again at half health — or end this run.`;
      if (yes) { yes.style.display = ""; yes.textContent = `Trade ${cost} ${w} for a life`; yes.onclick = onRevive; }
      if (no) { no.style.display = ""; no.textContent = "End run — start over"; no.onclick = onNewRun; }
    } else {
      if (txt) txt.textContent = `You have no memories left to trade with the Vault. Your journey ends here — but you will be called back.`;
      if (yes) { yes.style.display = ""; yes.textContent = "Start a New Run"; yes.onclick = onNewRun; }
      if (no) no.style.display = "none";
    }
    scr.classList.remove("hidden");
  }
  hideDeathModal() { document.getElementById("reviveScreen")?.classList.add("hidden"); }

  toggleInventory() {
    const inv = this.el.inventory;
    if (inv.classList.contains("hidden")) { this.renderInventory(); inv.classList.remove("hidden"); }
    else { inv.classList.add("hidden"); this._closeMenu(); }
  }
  closeInventory() { this.el.inventory.classList.add("hidden"); }
  get inventoryOpen() { return !this.el.inventory.classList.contains("hidden"); }

  // ---------- Locked-chest loot screen ----------
  get chestOpen() { return !!this.el.chestModal && !this.el.chestModal.classList.contains("hidden"); }
  openChest(chest) {
    if (!this.el.chestModal) return;
    this._chest = chest;
    this.renderChest();
    this.el.chestModal.classList.remove("hidden");
    // The sim is frozen while the chest is open, so V won't reach _handleInput —
    // handle the V-to-close toggle here (Esc/backdrop/Close are DOM-driven already).
    if (!this._chestKey) {
      this._chestKey = (e) => { if (this.chestOpen && (e.key === "v" || e.key === "V")) { e.stopPropagation(); this.closeChest(); } };
      document.addEventListener("keydown", this._chestKey, true);
    }
  }
  closeChest() { if (this.el.chestModal) this.el.chestModal.classList.add("hidden"); this._chest = null; }

  _chestSub(it) {
    if (!it) return "";
    switch (it.kind) {
      case "weapon": return `${it.dmgMin}–${it.dmgMax} dmg${it.element ? " · " + it.element : ""}`;
      case "armor": return `+${it.def} def · +${it.hp} hp`;
      case "potion": return it.potType === "mana" || it.mana ? `+${it.mana} MP` : `heals ${it.heal}`;
      case "food": return `heals ${it.heal || 0}`;
      case "coins": return "banked as gold";
      case "key": return "opens locked chests";
      case "treasure": return `worth ${it.value}g`;
      case "resource": return `material ×${it.qty || 1}`;
      default: return it.rarity ? it.rarity.name : "";
    }
  }
  _chestGlyph(it) {
    return ({ weapon: "⚔", armor: "🛡", potion: "🧪", food: "🍗", coins: "🪙", key: "🗝", treasure: "💎", resource: "⛏" })[it.kind] || "◆";
  }

  renderChest() {
    const m = this.el.chestModal; if (!m || !this._chest) return;
    const c = this._chest;
    const tier = c.tier ? c.tier.charAt(0).toUpperCase() + c.tier.slice(1) : "";
    if (this.el.chestTitle) this.el.chestTitle.textContent = `${tier} Chest`;
    const body = this.el.chestSlots; if (!body) return;
    body.innerHTML = "";
    const slots = c.contents || [];
    for (let i = 0; i < 5; i++) {
      const it = slots[i] || null;
      const cell = document.createElement("div");
      cell.className = "chest-slot" + (it ? "" : " empty");
      if (it) {
        const icon = this._icon(it);
        const rc = it.rarity ? it.rarity.color : "#cfc9b8";
        const name = it.kind === "coins" ? `${it.amount} Gold` : it.kind === "key" ? "Vault Key" : it.name;
        cell.style.setProperty("--rc", rc);
        cell.innerHTML =
          `<div class="chest-thumb">${icon ? `<img src="${icon}" alt="" draggable="false">` : `<span class="chest-glyph">${this._chestGlyph(it)}</span>`}</div>` +
          `<div class="chest-cname" style="color:${rc}">${name}</div>` +
          `<div class="chest-csub">${this._chestSub(it)}</div>` +
          `<div class="chest-take">Take</div>`;
        cell.onclick = () => this._takeChestSlot(i);
      } else {
        cell.innerHTML = `<div class="chest-thumb empty">·</div>`;
      }
      body.appendChild(cell);
    }
    if (this.el.chestTakeAll) this.el.chestTakeAll.disabled = !slots.some((s) => s);
  }

  _takeChestSlot(i) {
    if (!this._chest) return;
    const r = this.game.chestTake(this._chest, i);
    if (r === "empty") return;
    if (this.game.audio) this.game.audio.sfx("magic_heal", 0.4);
    if (r === "dropped") this.toast("Bag full — dropped at your feet.", 2200);
    if (this._chestEmpty()) { this.closeChest(); return; }   // emptied → close automatically
    this.renderChest();
  }
  _takeAllChest() {
    const c = this._chest; if (!c || !c.contents) return;
    let took = 0, dropped = 0;
    for (let i = 0; i < c.contents.length; i++) {
      if (!c.contents[i]) continue;
      const r = this.game.chestTake(c, i);
      if (r === "dropped") dropped++;
      if (r !== "empty") took++;
    }
    if (took && this.game.audio) this.game.audio.sfx("magic_heal", 0.4);
    if (dropped) this.toast(`Bag full — ${dropped} item${dropped > 1 ? "s" : ""} dropped at your feet.`, 2600);
    this.closeChest();   // everything taken (bagged or dropped) → close
  }
  _chestEmpty() { const c = this._chest; return !c || !c.contents || c.contents.every((s) => !s); }

  /** Refresh the signature-ability widget (glyph, cooldown sweep, mana). */
  _updateAbilityWidget(p, g) {
    const el = this.el.ability; if (!el) return;
    const show = g.state === "playing" && !g.inHub && !!p && !!g.character;
    el.style.display = show ? "" : "none";
    if (!show) return;
    const a = g.ability(); if (!a) return;
    const rank = g.abilityRank ? g.abilityRank() : 0;
    const sig = a.id + ":" + rank;
    if (this._abId !== sig) {   // rebuild static bits when the hero OR the ability rank changes
      this._abId = sig;
      el.querySelector(".ab-glyph").textContent = a.glyph;
      el.querySelector(".ab-name").textContent = a.name + (rank ? " " + "★".repeat(rank) : "");
      const mana = el.querySelector(".ab-mana");
      mana.textContent = (a.mana || 0) > 0 ? a.mana : "";
      mana.style.display = (a.mana || 0) > 0 ? "" : "none";
    }
    const onCd = p.abilityCd > 0;
    const noMana = (a.mana || 0) > 0 && p.mana < a.mana;
    const frac = onCd ? Math.max(0, Math.min(1, p.abilityCd / (p.abilityCdMax || a.cd))) : 0;
    el.querySelector(".ab-cd").style.height = (frac * 100) + "%";
    el.querySelector(".ab-count").textContent = onCd ? Math.ceil(p.abilityCd) : "";
    el.classList.toggle("ready", !onCd && !noMana);
    el.classList.toggle("nomana", !onCd && noMana);
  }

  _use(idx) {
    const it = this.game.player.inv[idx];
    if (!it) return;
    if (it.kind === "potion") useItem(this.game.player, idx);
    else equipItem(this.game.player, idx);
    this._sig = "";
    this._selected = null;   // bag indices shifted
    if (this.inventoryOpen) this.renderInventory();
  }

  _eatFood(idx) {
    const p = this.game.player;
    const it = p.inv[idx];
    if (!it || it.kind !== "food") return;
    // NO eating unless there is a free food slot — food fills a temporary buff
    // slot, and you can't heal off food while all three are occupied. (Closes
    // the "eat repeatedly with full slots" exploit.)
    const slot = p.foods.findIndex((f) => !f);
    if (slot < 0) {
      this._sig = "";
      if (this.game.effects) this.game.effects.text(p.pos, "Food slots full — wait for a buff to fade", "#e0a35a");
      if (this.inventoryOpen) this.renderInventory();
      return;
    }
    // eat: instant heal + fill the free buff slot with one unit (buff expires over time)
    if (it.heal) {
      const before = p.hp;
      p.hp = Math.min(p.maxHp, p.hp + it.heal);
      const gained = Math.round(p.hp - before);
      if (gained > 0 && this.game.effects) this.game.effects.text(p.pos, `+${gained}`, "#5fd35f");
    }
    const one = takeOne(p.inv, idx);   // pull a single unit off the stack
    p.setFood(slot, one);
    if (this.game.audio) this.game.audio.sfx("magic_heal", 0.4);
    this._sig = ""; this._selected = null;
    if (this.inventoryOpen) this.renderInventory();
  }

  _unequipFood(i) {
    const p = this.game.player, f = p.foods[i];
    if (!f) return;
    addItem(p.inv, f, p.invCap);   // back to bag (merges into a stack) if room, else discarded
    p.setFood(i, null);
    this._sig = ""; this.renderInventory();
  }

  _icon(item) { return this.game.icons ? this.game.icons.iconFor(item) : null; }

  _slot({ item, glyph, rarity, keyLabel, badge, count, equipped, empty, onClick, title, drag, drop }) {
    const s = document.createElement("div");
    s.className = "slot" + (equipped ? " equipped" : "") + (empty ? " empty" : "");
    if (title) s.title = title;
    const icon = item ? this._icon(item) : null;
    const visual = icon ? `<img class="icon" src="${icon}" alt="" draggable="false">` : `<span class="glyph">${glyph}</span>`;
    s.innerHTML =
      (keyLabel ? `<span class="key">${keyLabel}</span>` : "") +
      visual +
      (rarity ? `<span class="rar" style="border-color:${rarity}"></span>` : "") +
      (count && count > 1 ? `<span class="count">${count}</span>` : "") +
      (badge ? `<span class="badge">${badge}</span>` : "");
    if (onClick) s.onclick = onClick;
    // ---- drag-and-drop (Minecraft-style reorganize) ----
    if (drag) {
      s.draggable = true;
      s.addEventListener("dragstart", (e) => {
        this._drag = drag;
        try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "item"); } catch (_) {}
        s.classList.add("dragging");
      });
      s.addEventListener("dragend", () => {
        this._drag = null; s.classList.remove("dragging");
        document.querySelectorAll(".slot.drag-over").forEach((el) => el.classList.remove("drag-over"));
      });
    }
    if (drop) {
      s.addEventListener("dragover", (e) => { if (this._drag) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; s.classList.add("drag-over"); } });
      s.addEventListener("dragleave", () => s.classList.remove("drag-over"));
      s.addEventListener("drop", (e) => { e.preventDefault(); s.classList.remove("drag-over"); this._onDrop(drop); });
    }
    return s;
  }

  /** Resolve a completed drag from this._drag onto the drop target, then re-render. */
  _onDrop(dst) {
    const src = this._drag; this._drag = null;
    if (!src || !this.game.player) return;
    this._resolveDrop(src, dst);
    this._sig = ""; this._selected = null;
    if (this.inventoryOpen) this.renderInventory();
    this.renderHotbar();
  }

  _resolveDrop(src, dst) {
    const p = this.game.player, inv = p.inv;
    // ---- bag item onto an equip slot → equip (weapon/armor of matching kind) ----
    if (src.t === "bag" && (dst.t === "weapon" || dst.t === "armor")) {
      const it = inv[src.i]; if (!it) return;
      if (it.kind !== dst.t) return;                       // wrong gear type for this slot
      const r = equipItem(p, src.i);
      if (r && r.refused && this.game.effects) this.game.effects.text(p.pos, r.note || "The relic refuses your hand.", "#e0a35a");
      else if (this.game.audio) this.game.audio.sfx("magic_charge", 0.35);
      return;
    }
    // ---- bag food onto a food slot → eat it ----
    if (src.t === "bag" && dst.t === "food") {
      if (inv[src.i] && inv[src.i].kind === "food") this._eatFood(src.i);
      return;
    }
    // ---- food buff slot back into the bag → remove it ----
    if (src.t === "food" && dst.t === "bag") { this._unequipFood(src.i); return; }
    // ---- bag ↔ bag: move into an empty slot (keep the gap), merge, or swap ----
    if (src.t === "bag" && dst.t === "bag") {
      const i = src.i, j = dst.i;
      if (i === j) return;
      const a = inv[i]; if (!a) return;
      const b = inv[j];
      if (!b) { inv[j] = a; inv[i] = null; return; }   // dropped on an empty slot → move there, leave a gap
      // merge identical stacks
      if (isStackable(a) && isStackable(b) && stackKey(a) === stackKey(b)) {
        const room = MAX_STACK - stackCount(b);
        if (room > 0) {
          const move = Math.min(room, stackCount(a));
          b.qty = stackCount(b) + move;
          if (stackCount(a) - move <= 0) inv[i] = null; else a.qty = stackCount(a) - move;
          return;
        }
      }
      inv[i] = b; inv[j] = a;   // swap
    }
  }

  renderHotbar() {
    const p = this.game.player;
    const bar = this.el.hotbar;
    bar.innerHTML = "";
    const eq = document.createElement("div");
    eq.className = "group";
    eq.innerHTML = `<span class="group-label">EQUIPPED</span>`;
    eq.appendChild(this._slot({ item: p.weapon, glyph: weaponGlyph(p.weapon), rarity: p.weapon.rarity.color, equipped: true, badge: `${p.weapon.dmgMin}-${p.weapon.dmgMax}`, title: `${p.weapon.rarity.name} ${p.weapon.name}`, drop: { t: "weapon" } }));
    eq.appendChild(this._slot({ item: p.armor, glyph: gi("shield"), rarity: p.armor.rarity.color, equipped: true, badge: `+${p.armor.def}`, title: `${p.armor.rarity.name} ${p.armor.name}`, drop: { t: "armor" } }));
    bar.appendChild(eq);
    const sep = document.createElement("div"); sep.className = "sep"; bar.appendChild(sep);
    const bag = document.createElement("div");
    bag.className = "group";
    bag.innerHTML = `<span class="group-label">BAG (${slotCount(p.inv)}/${p.invCap})</span>`;
    for (let i = 0; i < HOTBAR_ITEM_SLOTS; i++) {
      const it = p.inv[i];
      if (it) {
        bag.appendChild(this._slot({
          item: it, glyph: itemGlyph(it), rarity: it.rarity ? it.rarity.color : "#888", keyLabel: i < 9 ? i + 1 : "",
          badge: badgeFor(it), count: stackCount(it),
          title: `${it.rarity ? it.rarity.name + " " : ""}${it.name}${stackCount(it) > 1 ? ` ×${stackCount(it)}` : ""}${isUsable(it) ? " — click to use · drag to move" : " — drag to move"}`,
          onClick: isUsable(it) ? () => this._use(i) : null,
          drag: { t: "bag", i }, drop: { t: "bag", i },
        }));
      } else bag.appendChild(this._slot({ glyph: "·", empty: true, drop: { t: "bag", i } }));
    }
    bar.appendChild(bag);
  }

  _selectedItem() {
    const p = this.game.player, s = this._selected;
    if (!s) return null;
    if (s.kind === "weapon") return p.weapon;
    if (s.kind === "armor") return p.armor;
    if (s.kind === "bag") return p.inv[s.idx] || null;
    return null;
  }

  _select(sel) { this._selected = sel; this.renderInventory(); }

  _isSel(kind, idx) {
    const s = this._selected;
    return s && s.kind === kind && (kind !== "bag" || s.idx === idx);
  }

  renderInventory() {
    const p = this.game.player;
    const inv = this.el.inventory;
    inv.innerHTML = `<h3>INVENTORY <button id="invTidy" class="inv-tidy-btn" title="Auto-stack &amp; sort the bag">⇅ Tidy</button></h3>` +
      `<div class="hint">Bag ${slotCount(p.inv)}/${p.invCap} &middot; click to inspect &middot; shift-click to use/equip &middot; drag to rearrange &middot; right-click for menu &middot; [E] to close</div>`;

    const body = document.createElement("div"); body.className = "inv-body";
    const left = document.createElement("div"); left.className = "inv-left";

    // equipped gear as real slots (weapon + armor), each selectable
    const eqRow = document.createElement("div"); eqRow.className = "equip-row";
    const wrap = (label, slotEl) => {
      const cell = document.createElement("div"); cell.className = "equip-cell";
      const l = document.createElement("span"); l.className = "equip-label"; l.textContent = label;
      cell.appendChild(l); cell.appendChild(slotEl); return cell;
    };
    const wSlot = this._slot({
      item: p.weapon, glyph: weaponGlyph(p.weapon), rarity: p.weapon.rarity.color, equipped: true,
      badge: `${p.weapon.dmgMin}-${p.weapon.dmgMax}`, title: `${p.weapon.rarity.name} ${p.weapon.name}`,
      onClick: () => this._select({ kind: "weapon" }), drop: { t: "weapon" },
    });
    if (this._isSel("weapon")) wSlot.classList.add("selected");
    const aSlot = this._slot({
      item: p.armor, glyph: gi("shield"), rarity: p.armor.rarity.color, equipped: true,
      badge: `+${p.armor.def}`, title: `${p.armor.rarity.name} ${p.armor.name}`,
      onClick: () => this._select({ kind: "armor" }), drop: { t: "armor" },
    });
    if (this._isSel("armor")) aSlot.classList.add("selected");
    eqRow.appendChild(wrap("WEAPON", wSlot));
    eqRow.appendChild(wrap("ARMOR", aSlot));
    left.appendChild(eqRow);
    const divider = document.createElement("div"); divider.className = "inv-divider"; divider.textContent = "BAG";
    left.appendChild(divider);

    // fixed slot grid up to carrying capacity (filled + empty)
    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:repeat(6,58px);gap:8px;justify-content:center;margin-top:6px;";
    for (let i = 0; i < p.invCap; i++) {
      const it = p.inv[i];
      if (it) {
        const el = this._slot({
          item: it, glyph: itemGlyph(it), rarity: it.rarity ? it.rarity.color : "#888", badge: badgeFor(it),
          count: stackCount(it),
          title: `${it.rarity ? it.rarity.name + " " : ""}${it.name}${stackCount(it) > 1 ? ` ×${stackCount(it)}` : ""}`,
          onClick: (ev) => { if (ev && ev.shiftKey && isUsable(it)) this._use(i); else this._select({ kind: "bag", idx: i }); },
          drag: { t: "bag", i }, drop: { t: "bag", i },
        });
        if (this._isSel("bag", i)) el.classList.add("selected");
        el.oncontextmenu = (ev) => { ev.preventDefault(); this._select({ kind: "bag", idx: i }); this._showItemMenu(ev.clientX, ev.clientY, i); };
        grid.appendChild(el);
      } else {
        grid.appendChild(this._slot({ glyph: "", empty: true, drop: { t: "bag", i } }));
      }
    }
    left.appendChild(grid);

    // ---- food slots (3, stacking buffs) ----
    const fdiv = document.createElement("div"); fdiv.className = "inv-divider"; fdiv.textContent = "FOOD — stacking buffs";
    left.appendChild(fdiv);
    const foodRow = document.createElement("div"); foodRow.className = "food-row";
    for (let i = 0; i < 3; i++) {
      const f = p.foods[i];
      const secs = f && p.foodTimers ? Math.max(0, Math.ceil(p.foodTimers[i])) : 0;
      const cell = this._slot({
        item: f || null, glyph: "", rarity: f && f.rarity ? f.rarity.color : null,
        badge: f ? `${secs}s` : "",
        title: f ? `${f.name} — buff fades in ${secs}s (click or drag out to remove)` : "empty food slot — drag food here to eat", empty: !f,
        drop: { t: "food", i }, drag: f ? { t: "food", i } : null,
      });
      if (f) cell.onclick = () => this._unequipFood(i);
      foodRow.appendChild(cell);
    }
    left.appendChild(foodRow);
    const b = p.buffs || {};
    const parts = [];
    if (b.maxStam) parts.push(`+${b.maxStam} max stam`);
    if (b.stamRegen) parts.push(`+${b.stamRegen} stam regen`);
    if (b.maxMana) parts.push(`+${b.maxMana} max mana`);
    if (b.manaRegen) parts.push(`+${b.manaRegen} mana regen`);
    const bsum = document.createElement("div"); bsum.className = "food-buffs";
    bsum.textContent = parts.length ? "Active buffs: " + parts.join(", ") : "No food buffs. Right-click food to eat.";
    left.appendChild(bsum);
    body.appendChild(left);

    // details sidebar
    const side = document.createElement("div"); side.className = "inv-sidebar";
    side.innerHTML = this._sidebarHTML(this._selectedItem());
    body.appendChild(side);

    inv.appendChild(body);

    const tidyBtn = inv.querySelector("#invTidy");
    if (tidyBtn) tidyBtn.onclick = () => {
      tidyBag(p.inv, p.invCap);
      this._sig = ""; this._selected = null;
      this.renderInventory(); this.renderHotbar();
      if (this.game.audio) this.game.audio.sfx("magic_charge", 0.25);
    };
  }

  _typeLabel(it) {
    const rar = it.rarity ? it.rarity.name + " " : "";
    switch (it.kind) {
      case "weapon": return `${rar}Weapon · ${it.wtype === "ranged" ? (it.magic ? "Magic" : "Ranged") : "Melee"}`;
      case "armor": return `${rar}Armor`;
      case "potion": return it.potType === "mana" ? "Mana Potion" : "Consumable";
      case "food": return "Food";
      case "coins": return "Currency";
      case "resource": return `${rar}Material`;
      case "fragment": return "Memory Fragment";
      case "treasure": return `${rar}Relic · Treasure`;
      default: return rar || "Item";
    }
  }

  _sidebarHTML(it) {
    if (!it) return `<div class="side-empty">Select an item to inspect it.</div>`;
    const col = it.rarity ? it.rarity.color : "#e8e2d0";
    const row = (a, b, c) => `<div class="stat-row"><span>${a}</span><span${c ? ` style="color:${c}"` : ""}>${b}</span></div>`;
    let stats = "";
    if (it.kind === "weapon") {
      stats += row("Damage", `${it.dmgMin}–${it.dmgMax}`);
      stats += row("Attack", it.wtype === "ranged" ? "Ranged" : "Melee");
      stats += row("Reach", (it.range || 0).toFixed(1));
      stats += row("Cooldown", `${it.cd}s`);
      if (it.aoe) stats += row("AoE radius", it.aoe.toFixed(1));
      if (it.element) {
        const e = ELEMENTS[it.element];
        stats += row("Enchant", `${e ? e.name : it.element} (${(it.elementPower || 0).toFixed(1)})`, e ? e.color : col);
      }
      if (it.special) stats += `<div class="side-hint" style="color:#f0a63c;font-weight:bold">✦ ${it.special}</div>`;
    } else if (it.kind === "armor") {
      stats += row("Defense", `+${it.def}`);
      stats += row("Bonus HP", `+${it.hp}`);
    } else if (it.kind === "potion") {
      if (it.potType === "mana" || it.mana) stats += row("Restores", `+${it.mana} MP`, "#4aa3ff");
      else stats += row("Restores", `+${it.heal} HP`, "#5fd35f");
    } else if (it.kind === "food") {
      const b = it.buffs || {};
      if (it.heal) stats += row("Heals", `+${it.heal} HP`, "#5fd35f");
      if (b.maxStam) stats += row("Max stamina", `+${b.maxStam}`);
      if (b.stamRegen) stats += row("Stamina regen", `+${b.stamRegen}/s`);
      if (b.maxMana) stats += row("Max mana", `+${b.maxMana}`);
      if (b.manaRegen) stats += row("Mana regen", `+${b.manaRegen}/s`);
      stats += `<div class="side-hint">Eat to heal instantly and fill a food slot — buffs stack (up to 3).</div>`;
    } else if (it.kind === "coins") {
      stats += row("Value", `${it.amount} gold`);
    } else if (it.kind === "resource") {
      stats += row("Quantity", `x${stackCount(it)}`);
      stats += `<div class="side-hint">Crafting material.</div>`;
    } else if (it.kind === "treasure") {
      stats += row("Value", `${it.value} gold`, "#f0c34b");
      stats += `<div class="side-hint" style="font-style:italic">${it.flavor || "A relic of the Vault."}</div>`;
      stats += `<div class="side-hint">Sell it at the Shop — treasures barely mark down.</div>`;
    } else if (it.kind === "fragment") {
      const m = getMemory(it.fragId);
      const cx = it.codexId ? getCodex(it.codexId) : null;
      if (cx) stats += row("Codex", `${it.codexId} · ${cx.title}`, cx.color);
      stats += `<div class="side-hint" style="color:#d9c68a;font-style:italic">${it.memText || (m ? m.text : it.flavor || "")}</div>`;
      stats += `<div class="side-hint">Offer it at the Helix to light its codex. (Using it here offers it too.)</div>`;
    }

    if (stackCount(it) > 1 && it.kind !== "resource") stats += row("Stack", `×${stackCount(it)}`);

    // Relic Memory panel (weapons + armor)
    let mem = "";
    if (it.kind === "weapon" || it.kind === "armor") {
      const t = memTier(it), pct = Math.round(memProgress(it) * 100);
      mem += `<div class="side-sep"></div>`;
      mem += `<div class="stat-row"><span>Memory</span><span style="color:${t.color}">${memSummary(it) || t.name}</span></div>`;
      mem += `<div class="mem-bar"><span style="width:${pct}%;background:${t.color}"></span></div>`;
      if (it.kind === "weapon") mem += row("Awakened power", `+${Math.round(t.dmg * 100)}% dmg`, t.color);
      else mem += row("Awakened ward", `+${t.def} def, +${t.hp} hp`, t.color);
      if (it.named) {
        const aff = affinityFor(it, this.game.player ? this.game.player.classId : null, this.game.player && this.game.player.attuneAny);
        mem += `<div class="side-hint" style="color:${aff.wield ? (aff.mult > 1 ? "#7bd645" : "#d9c68a") : "#ff8a7a"}">${aff.note}</div>`;
        if (it.flavor) mem += `<div class="side-hint" style="font-style:italic">${it.flavor}</div>`;
      }
    }

    return `<div class="side-name" style="color:${col}">${it.named ? "✦ " : ""}${it.name}</div>` +
      `<div class="side-type">${this._typeLabel(it)}${it.named ? " · Named Relic" : ""}</div>` +
      `<div class="side-stats">${stats}${mem}</div>` +
      `<div class="side-hint">Right-click the item for Equip / Use / Discard.</div>`;
  }

  _closeMenu() {
    if (this._menu) { this._menu.remove(); this._menu = null; }
    if (this._menuClose) { window.removeEventListener("mousedown", this._menuClose); this._menuClose = null; }
  }

  _showItemMenu(x, y, idx) {
    this._closeMenu();
    const p = this.game.player;
    const it = p.inv[idx];
    if (!it) return;
    const menu = document.createElement("div");
    menu.className = "ctx-menu";
    menu.style.left = x + "px"; menu.style.top = y + "px";
    const usable = it.kind === "weapon" || it.kind === "armor" || it.kind === "potion";
    const isFood = it.kind === "food";
    const isFrag = it.kind === "fragment";
    const useLabel = it.kind === "potion" ? "Use" : "Equip";
    menu.innerHTML =
      (usable ? `<div class="ctx-item" data-a="use">${useLabel}</div>` : "") +
      (isFood ? `<div class="ctx-item" data-a="eat">Eat (add to food slot)</div>` : "") +
      (isFrag ? `<div class="ctx-item" data-a="turnin">Turn in (record memory)</div>` : "") +
      `<div class="ctx-item danger" data-a="discard">Discard</div>`;
    // delegated handler so the re-rendered confirm buttons work too
    menu.onclick = (e) => {
      const t = e.target.closest("[data-a]"); if (!t) return;
      const a = t.dataset.a;
      if (a === "use" && usable) { this._use(idx); this._closeMenu(); }
      else if (a === "eat" && isFood) { this._eatFood(idx); this._closeMenu(); }
      else if (a === "turnin" && isFrag) { this.game._useSlot(idx); this._closeMenu(); }
      else if (a === "discard") {
        // swap the menu into an in-game confirm (no browser alert)
        menu.innerHTML =
          `<div class="ctx-confirm">Discard <b>${it.name}</b>?</div>` +
          `<div class="ctx-item danger" data-a="do-discard">Yes, discard</div>` +
          `<div class="ctx-item" data-a="keep">Keep it</div>`;
        this._clampMenu(menu);   // taller/wider now — keep it on-screen
      }
      else if (a === "do-discard") {
        p.inv[idx] = null; this._sig = ""; this._selected = null;
        this._closeMenu(); this.renderInventory();
      }
      else if (a === "keep") { this._closeMenu(); }
    };
    document.body.appendChild(menu);
    this._menu = menu;
    this._clampMenu(menu);   // keep the popup fully on-screen (no clipped text)
    setTimeout(() => {
      this._menuClose = (e) => { if (!menu.contains(e.target)) this._closeMenu(); };
      window.addEventListener("mousedown", this._menuClose);
    }, 0);
  }

  /** Shift a popup so it never spills off the viewport edges. */
  _clampMenu(menu) {
    const m = 8, r = menu.getBoundingClientRect();
    let nx = r.left, ny = r.top;
    if (r.right > window.innerWidth - m) nx = window.innerWidth - r.width - m;
    if (r.bottom > window.innerHeight - m) ny = window.innerHeight - r.height - m;
    menu.style.left = Math.max(m, nx) + "px";
    menu.style.top = Math.max(m, ny) + "px";
  }

  update() {
    const g = this.game, p = g.player;
    if (!p) return;
    this.el.healthFill.style.width = Math.max(0, p.hp / p.maxHp) * 100 + "%";
    this.el.healthText.textContent = `${Math.ceil(p.hp)} / ${p.maxHp}`;
    if (this.el.staminaFill) {
      this.el.staminaFill.style.width = Math.max(0, p.stamina / p.maxStamina) * 100 + "%";
      this.el.staminaText.textContent = `${Math.ceil(p.stamina)} / ${p.maxStamina}`;
    }
    if (this.el.manaBar) {
      const mm = p.maxMana || 0;
      this.el.manaBar.style.display = mm > 0 ? "" : "none";
      if (mm > 0) {
        this.el.manaFill.style.width = Math.max(0, p.mana / mm) * 100 + "%";
        this.el.manaText.textContent = `${Math.ceil(p.mana)} / ${mm}`;
      }
    }
    this.el.gear.innerHTML =
      `<span class="stat" style="color:${p.weapon.rarity.color}">${weaponGlyph(p.weapon)} ${p.weapon.name} (${p.weapon.dmgMin}-${p.weapon.dmgMax} dmg)</span>` +
      `<span class="stat" style="color:${p.armor.rarity.color}">${gi("shield")} ${p.armor.name} (+${p.armor.def} def, +${p.armor.hp} hp)</span>`;
    this._updateAbilityWidget(p, g);
    this.el.floor.textContent = `LEVEL ${g.floor}${g.theme ? " · " + g.theme.name.toUpperCase() : ""}`;
    const alive = g.combat.enemies.filter((e) => !e.dead).length;
    this.el.enemies.textContent = alive > 0 ? `${alive} enemies remain` : `Cleared — find the portal`;
    const not = (g.meta && g.meta.notoriety) || 0;
    const tier = not > 800 ? "The Vault knows you" : not > 400 ? "The Vault remembers you" : not > 150 ? "The Vault watches you" : not > 50 ? "The Vault has noticed you" : "Unremembered";
    // always-visible currency badge: gold + Vault Keys
    if (this.el.currency) this.el.currency.innerHTML =
      `<span class="cur gold">${gi("coins")} ${p.gold || 0}</span><span class="cur keys">${gi("key")} ${p.keys || 0}</span>`;
    this.el.kills.innerHTML = `Kills: ${g.totalKills}   ✦ ${tier}`;
    this._renderPets();
    if (this.el.heroLevel && g.profile) {
      this.el.heroLevel.textContent = `Hero Lv ${g.profile.level}`;
      const need = g.save.xpNeeded(g.profile.level);
      if (this.el.xpFill) this.el.xpFill.style.width = Math.max(0, Math.min(1, g.profile.xp / need)) * 100 + "%";
    }
    // minimap legend — visibility mirrors the minimap; content depends on context
    if (this.el.minimapLegend) {
      const show = this.el.minimap && !this.el.minimap.classList.contains("hidden");
      this.el.minimapLegend.classList.toggle("hidden", !show);
      if (show && this._legendHub !== g.inHub) {
        this._legendHub = g.inHub;
        const item = (c, label, dia) => `<span class="mm-item"><span class="${dia ? "mm-diamond" : "mm-dot"}" style="--c:${c}"></span>${label}</span>`;
        this.el.minimapLegend.innerHTML = g.inHub
          ? item("#5fd35f", "You") + item("#ffe8a3", "Helix") + item("#e0975a", "Forge") + item("#ffd24a", "Shop") + item("#7bd645", "Home") + item("#c77dff", "Research")
          : item("#5fd35f", "You") + item("#ffd24a", "Exit", true);
      }
    }

    const sig = p.weapon.name + p.weapon.rarity.name + "|" + p.armor.name + p.armor.rarity.name + "|" +
      p.inv.map((i) => i ? i.name + i.rarity.name + (i.qty || "") : "-").join(",");
    if (sig !== this._sig) { this._sig = sig; this.renderHotbar(); }
  }
}
